import { tz } from "@date-fns/tz";
import {
  Box,
  Icon,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { addDays, isSameDay } from "date-fns";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { useGetEarnings } from "api";
import { useFiltersContext } from "components/contexts";
import { StickyTable, Widget } from "components/elements/mui";
import { formatKwh, formatPln } from "components/elements/widgets/Earnings/format";
import { useFormatMessage } from "lang";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { pulseSx } from "styles/animations";

// The selected day priced: the app's production forecast per hour next to
// the RDN price (Tradea's FIX1, or TGE's hourly Fixing I until Tradea has
// it), the PSE price (settled, preliminary or the nowcast — the nowcast
// pulses like predicted prices elsewhere) and the earnings that follow.
export const ForecastEarnings = () => {
  const formatMessage = useFormatMessage();
  const intl = useIntl();
  const { date } = useFiltersContext();
  const [searchParams] = useSearchParams();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = tz(selectedPowerPlant?.timezone || "Europe/Warsaw");
  const now = new Date();
  const withinPricedDays =
    isSameDay(date.day, now, { in: timezone }) || isSameDay(date.day, addDays(now, 1), { in: timezone });
  const show = searchParams.get("param") === "earnings" && withinPricedDays;
  const { data, isLoading } = useGetEarnings(date.day, show);
  // Hours unfolded into their four quarters, by index in the day's hours.
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  if (!show) return null;
  if (!isLoading && (!data || !data.configured)) return null;
  const day = data?.configured ? data : null;
  const hours = (day?.hours ?? []).map((h, i) => ({ h, i })).filter(({ h }) => (h.ourForecastKwh ?? 0) > 0);
  const quiet = { color: "text.secondary", fontSize: "0.8rem" } as const;
  const money = (v: number | null, digits = 2) => (v === null ? "—" : formatPln(intl, v, digits));

  return (
    <Widget
      title={formatMessage("forecast.pricesTitle")}
      loading={isLoading}
      size={{ xs: 12 }}
      content={
        !day ? (
          <Typography color="text.secondary">{formatMessage("prices.noData")}</Typography>
        ) : (
          <Stack gap={1.5}>
            {hours.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                {formatMessage("prices.noData")}
              </Typography>
            ) : (
              <StickyTable>
                  <TableHead>
                    <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
                      <TableCell>{formatMessage("prices.tableTime")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatMessage("forecast.ourKwh")}
                      </TableCell>
                      <TableCell align="right" sx={quiet}>{formatMessage("prices.rdn")}</TableCell>
                      <TableCell align="right" sx={quiet}>{formatMessage("prices.pse")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatMessage("forecast.expectedPln")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hours.map(({ h, i }) => (
                      <Fragment key={`${i}-${h.time}`}>
                        <TableRow
                          hover
                          onClick={() => toggle(i)}
                          sx={{ cursor: "pointer", "& td": open.has(i) ? { borderBottom: 0 } : undefined }}
                        >
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Stack direction="row" alignItems="center" gap={0.25}>
                              <Icon
                                sx={{
                                  fontSize: 16,
                                  color: "text.secondary",
                                  transition: "transform 150ms",
                                  transform: open.has(i) ? "rotate(90deg)" : "none",
                                }}
                              >
                                chevron_right
                              </Icon>
                              {h.time}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {h.ourForecastKwh === null ? "—" : formatKwh(intl, h.ourForecastKwh)}
                          </TableCell>
                          <TableCell align="right" sx={quiet}>{money(h.fix1)}</TableCell>
                          <TableCell align="right" sx={quiet}>
                            <Box component="span" sx={h.croSource === "forecast" || !h.complete ? pulseSx() : undefined}>
                              {money(h.cro)}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <Box
                              component="span"
                              sx={{ color: h.expectedOursPln !== null && h.expectedOursPln < 0 ? "error.main" : undefined }}
                            >
                              {money(h.expectedOursPln)}
                            </Box>
                          </TableCell>
                        </TableRow>
                        {open.has(i) &&
                          (day?.quarters ?? []).slice(i * 4, i * 4 + 4).map((q, j) => (
                            <TableRow
                              key={q.start}
                              sx={{
                                backgroundColor: "action.hover",
                                "& td": { fontSize: "0.75rem", py: 0.25, ...(j < 3 ? { borderBottom: 0 } : {}) },
                              }}
                            >
                              <TableCell sx={{ whiteSpace: "nowrap", pl: 4.5, color: "text.secondary" }}>{q.time}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {q.ourForecastKwh === null ? "—" : formatKwh(intl, q.ourForecastKwh)}
                              </TableCell>
                              <TableCell align="right" sx={quiet}>{money(q.fix1)}</TableCell>
                              <TableCell align="right" sx={quiet}>
                                <Box component="span" sx={q.croSource === "forecast" ? pulseSx() : undefined}>{money(q.cro)}</Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                <Box
                                  component="span"
                                  sx={{ color: q.expectedOursPln !== null && q.expectedOursPln < 0 ? "error.main" : undefined }}
                                >
                                  {money(q.expectedOursPln)}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                      </Fragment>
                    ))}
                  </TableBody>
              </StickyTable>
            )}
            <Typography variant="caption" color="text.secondary">
              {formatMessage("forecast.pricesHint")}
            </Typography>
          </Stack>
        )
      }
    />
  );
};
